import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/layout/Header.jsx';
import Footer from '../components/layout/Footer.jsx';
import { TopAnnouncementBar, SubAnnouncementBar } from '../components/layout/AnnouncementBar.jsx';
import { useSettings } from '../hooks/useSettings.jsx';
import { Toaster } from 'sonner';
import { API_URL } from '../utils/apiConfig';

const MainLayout = ({ children }) => {
  const { header, isLoading } = useSettings();
  const location = useLocation();

  // Bulletproof scroll-lock cleanup: on every route change, make sure no
  // leftover body lock (from a drawer/modal that didn't clean up) survives.
  useEffect(() => {
    document.body.classList.remove('overflow-hidden', 'sidebar-open');
    document.body.style.overflow = '';
  }, [location.pathname]);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  let fallbackHeaderHeight = 84;
  if (!isLoading && header) {
    if (header.topBar?.enabled) fallbackHeaderHeight += 40;
    if (header.subBar?.enabled) fallbackHeaderHeight += 36;
    if (header.navSubBar?.enabled) fallbackHeaderHeight += 52;
  }

  useEffect(() => {
    const node = headerRef.current;
    if (!node) return undefined;

    const updateHeaderHeight = () => {
      setHeaderHeight(node.getBoundingClientRect().height);
    };

    updateHeaderHeight();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => updateHeaderHeight())
      : null;

    resizeObserver?.observe(node);
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, [header, isLoading]);

  useEffect(() => {
    const visited = sessionStorage.getItem('visited');
    if (!visited) {
      fetch(`${API_URL}/settings/record-visit`, {
        method: 'POST',
      })
        .then(() => {
          sessionStorage.setItem('visited', 'true');
        })
        .catch((err) => console.error('Failed to record visit:', err));
    }
  }, []);

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ '--header-height': `${headerHeight || fallbackHeaderHeight}px` }}
    >
      <div ref={headerRef} className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-100">
        <TopAnnouncementBar />
        <Header />
        <SubAnnouncementBar />
      </div>
      <main
        className="flex-grow pb-4 sm:pb-12"
        style={{ paddingTop: `${headerHeight || fallbackHeaderHeight}px` }}
      >
        {children}
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
};

export default MainLayout;
